/** EFFET : choix de la source du visage local (webcam, ou visage synthétique en e2e). */
import type { Delegate } from './delegate';
import { preferredDelegate } from './delegatePreference';
import { createCameraFaceSource } from './faceTracker';
import type { FaceSource } from './faceSource';
import { createSyntheticFaceSource, type SyntheticExpression } from './syntheticFace';
import { createCameraHandSource } from './handTracker';
import { createSyntheticHandSource, type HandSource, type SyntheticGesture } from './syntheticHand';

/**
 * `?fakeFace=synthetic` n'est honoré que dans un build e2e (`VITE_E2E=true`) :
 * en production, la détection tourne toujours sur la vraie caméra.
 * `delegate` : imposé par le diagnostic (mesure) ; sinon la préférence mémorisée sur l'appareil.
 */
export function createLocalFaceSource(video: HTMLVideoElement, delegate?: Delegate): FaceSource {
  if (import.meta.env['VITE_E2E'] === 'true') {
    const fake = new URLSearchParams(location.search).get('fakeFace');
    if (fake === 'synthetic') {
      const source = createSyntheticFaceSource();
      // Pilotage depuis Playwright : window.__barbichetteTest.setExpression('laugh').
      addTestHook('setExpression', (e: SyntheticExpression) => {
        source.setExpression(e);
      });
      return source;
    }
  }
  return createCameraFaceSource({ video, delegate: delegate ?? preferredDelegate() });
}

/**
 * Visage de l'AMI, analysé sur la vidéo reçue (placement du filtre, jamais le jeu).
 * En e2e synthétique : visage synthétique piloté par `setRemoteExpression`.
 */
export function createRemoteFaceSource(video: HTMLVideoElement, maxFps: () => number): FaceSource {
  if (import.meta.env['VITE_E2E'] === 'true') {
    const fake = new URLSearchParams(location.search).get('fakeFace');
    if (fake === 'synthetic') {
      const source = createSyntheticFaceSource({ fps: 15 });
      addTestHook('setRemoteExpression', (e: SyntheticExpression) => {
        source.setExpression(e);
      });
      return source;
    }
  }
  return createCameraFaceSource({ video, maxFps, delegate: preferredDelegate() });
}

/** Main LOCALE (engagement par geste). En e2e synthétique : pilotée par `setHand`. */
export function createLocalHandSource(video: HTMLVideoElement): HandSource {
  if (import.meta.env['VITE_E2E'] === 'true') {
    const fake = new URLSearchParams(location.search).get('fakeFace');
    if (fake === 'synthetic') {
      const source = createSyntheticHandSource();
      addTestHook('setHand', (g: SyntheticGesture) => {
        source.setGesture(g);
      });
      return source;
    }
  }
  return createCameraHandSource(video);
}

function addTestHook(name: string, fn: (value: never) => void): void {
  const w = window as unknown as { __barbichetteTest?: Record<string, unknown> };
  w.__barbichetteTest = { ...w.__barbichetteTest, [name]: fn };
}
